import React, { useMemo } from 'react';
import { GridColDef, GridRenderCellParams, GridRowsProp } from '@mui/x-data-grid';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import CAppDataGrid from '../../../../app/components/CAppDataGrid';
import AppFormatDate from '../../../../app/features/format/AppFormatDate';
import { INotificationRecipientQueue } from '../../interfaces/INotificationMonitoring';
import langAdminNotificationMonitoring from '../../langs/langAdminNotificationMonitoring';

type CAdminNotificationRecipientTableProps = {
	recipients: INotificationRecipientQueue[];
	onSelect: (email: string) => void;
};

const CAdminNotificationRecipientTable = (props: CAdminNotificationRecipientTableProps) => {
	const { recipients, onSelect } = props;
	const { t } = useTranslation();

	const rows: GridRowsProp = useMemo(
		() =>
			recipients.map((x) => ({
				id: x.email,
				email: x.email,
				name: [x.firstName, x.lastName].filter(Boolean).join(' '),
				planned: x.planned,
				sentToday: x.sentToday,
				remainingQuotaToday: x.remainingQuotaToday,
				inError: x.inError,
				oldestPlannedReleaseDate: x.oldestPlannedReleaseDate,
				estimatedDrainDate: x.estimatedDrainDate,
			})),
		[recipients],
	);

	const numberColumn = (field: string, tag: string): GridColDef => ({
		field,
		headerName: t(tag),
		type: 'number',
		width: 150,
		align: 'center',
		headerAlign: 'center',
	});

	const dateColumn = (field: string, tag: string, description?: string): GridColDef => ({
		field,
		headerName: t(tag),
		description,
		width: 190,
		align: 'center',
		headerAlign: 'center',
		renderCell: (params: GridRenderCellParams) => (params.value ? <AppFormatDate date={params.value} /> : '-'),
	});

	const columns: GridColDef[] = [
		{
			field: 'email',
			headerName: t(langAdminNotificationMonitoring.column_email.tag),
			minWidth: 220,
			flex: 1,
		},
		{
			field: 'name',
			headerName: t(langAdminNotificationMonitoring.column_name.tag),
			width: 180,
		},
		numberColumn('planned', langAdminNotificationMonitoring.column_planned.tag),
		numberColumn('sentToday', langAdminNotificationMonitoring.column_sent_today.tag),
		numberColumn('remainingQuotaToday', langAdminNotificationMonitoring.column_remaining_quota_today.tag),
		numberColumn('inError', langAdminNotificationMonitoring.column_in_error.tag),
		dateColumn('oldestPlannedReleaseDate', langAdminNotificationMonitoring.column_oldest_planned_release_date.tag),
		dateColumn(
			'estimatedDrainDate',
			langAdminNotificationMonitoring.column_estimated_drain_date.tag,
			t(langAdminNotificationMonitoring.estimated_drain_date_help.tag),
		),
	];

	return (
		<Box>
			<CAppDataGrid
				className="admin-notification-monitoring-recipients-table"
				rows={rows}
				columns={columns}
				autoHeight
				pagination
				onRowClick={(params) => onSelect(String(params.id))}
				sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }}
			/>
		</Box>
	);
};

export default CAdminNotificationRecipientTable;
